import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, ScanLine } from "lucide-react";

const statusMeta = {
  draft: { label: "Brouillon", cls: "bg-[#C28E3A]/10 text-[#C28E3A]" },
  sent: { label: "Envoyée", cls: "bg-[#2E4F36]/10 text-[#2E4F36]" },
};

export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async (f) => {
    const params = f && f !== "all" ? { status: f } : {};
    const { data } = await api.get("/invoices", { params });
    setInvoices(data);
  };

  useEffect(() => { load(filter); }, [filter]);

  return (
    <div className="p-6 md:p-12" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5E2129] mb-2">Gestion</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A]">Factures</h1>
        </div>
        <button data-testid="invoices-scan-cta" onClick={() => navigate("/scanner")} className="inline-flex items-center gap-3 bg-[#5E2129] hover:bg-[#4A1A20] text-white px-6 py-3 rounded-full font-semibold transition-colors duration-200">
          <ScanLine className="w-5 h-5" /> Nouveau ticket
        </button>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-[#F1EADE]">
          <TabsTrigger value="all" data-testid="filter-all">Toutes</TabsTrigger>
          <TabsTrigger value="draft" data-testid="filter-draft">À valider</TabsTrigger>
          <TabsTrigger value="sent" data-testid="filter-sent">Envoyées</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="bg-white border border-[#EBE5D9] rounded-xl overflow-hidden">
        {invoices.length === 0 ? (
          <div className="py-20 text-center">
            <FileText className="w-10 h-10 text-[#EBE5D9] mx-auto mb-3" />
            <p className="text-[#666]">Aucune facture pour l'instant.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[640px]">
            <thead className="bg-[#FDFBF7] border-b border-[#EBE5D9]">
              <tr className="text-xs uppercase tracking-wider text-[#666]">
                <th className="px-6 py-4 font-semibold">N°</th>
                <th className="px-6 py-4 font-semibold">Client</th>
                <th className="px-6 py-4 font-semibold">Date</th>
                <th className="px-6 py-4 font-semibold">Statut</th>
                <th className="px-6 py-4 font-semibold">Bexio</th>
                <th className="px-6 py-4 font-semibold text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const m = statusMeta[inv.status] || statusMeta.draft;
                return (
                  <tr
                    key={inv.id}
                    data-testid={`invoice-row-${inv.id}`}
                    onClick={() => navigate(`/factures/${inv.id}`)}
                    className="border-b border-[#EBE5D9] last:border-0 hover:bg-[#FDFBF7] cursor-pointer transition-colors duration-150"
                  >
                    <td className="px-6 py-4 font-mono text-sm text-[#666]">{inv.invoice_number}</td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-[#1A1A1A]">{inv.client_name}</p>
                      {inv.company && <p className="text-xs text-[#666]">{inv.company}</p>}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#666]">{inv.date}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${m.cls}`}>{m.label}</span>
                    </td>
                    <td className="px-6 py-4" data-testid={`invoice-bexio-status-${inv.id}`}>
                      {inv.bexio_synced ? (
                        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#2E4F36]/10 text-[#2E4F36]">Synchronisée ✓</span>
                      ) : inv.bexio_error ? (
                        <span title={inv.bexio_error} className="text-xs font-semibold px-3 py-1 rounded-full bg-red-100 text-red-700">Erreur</span>
                      ) : (
                        <span className="text-xs font-medium px-3 py-1 rounded-full bg-[#EBE5D9]/60 text-[#666]">Non envoyée</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-serif text-lg text-[#5E2129]">CHF {inv.total?.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
